import React, {
  createContext,
  type Dispatch,
  type SetStateAction,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
} from 'react'
import { getTreeStartingPid, SelectPersonOverlay } from '@fs/zion-tree-groups'
import { type PID, type GroupType, type TreeId } from '@fs/zion-tree-types'
import { useOverlay, type UseOverlayReturnOptions } from '@fs/zion-ui'

type StartingPersonValue = {
  loading: boolean
  startingPersonId?: PID
  setStartingPersonId: Dispatch<SetStateAction<PID | undefined>>
  refetchStartingPersonPid: () => void
  startingPersonOverlay: UseOverlayReturnOptions
}

const StartingPersonContext = createContext<StartingPersonValue | undefined>(undefined)

export const useStartingPerson = (): StartingPersonValue => {
  const context = useContext(StartingPersonContext)
  if (!context) {
    throw new Error('useStartingPerson must be used within a StartingPersonProvider')
  }
  return context
}

type StartingPersonProviderProps = {
  treeId?: TreeId
  types?: GroupType[]
  children: ReactNode
}

export default function StartingPersonProvider({ treeId, types, children }: StartingPersonProviderProps): JSX.Element {
  const [startingPersonId, setStartingPersonId] = useState<PID>()
  const [loading, setLoading] = useState(true)
  const isTypeTree = types?.includes('PRIVATE_PERSON_SHARING') || types?.includes('RESEARCHER_TREE')
  const lastTreeStartingPersonDataWasFetched = useRef<TreeId | false>(false)
  const isCurrent = useRef<boolean>()
  const startingPersonOverlay = useOverlay()

  const onSelectStartingPerson = useCallback<NonNullable<ComponentProps<typeof SelectPersonOverlay>['onSelect']>>(
    (person) => {
      if (person.id !== 'UNKNOWN') {
        setStartingPersonId(person.id)
      }
      startingPersonOverlay.close()
    },
    [setStartingPersonId, startingPersonOverlay]
  )

  useEffect(() => {
    isCurrent.current = true
    return () => {
      isCurrent.current = false
    }
  })

  const refetchStartingPersonPid = useCallback(() => {
    lastTreeStartingPersonDataWasFetched.current = false
  }, [])

  if (treeId && lastTreeStartingPersonDataWasFetched.current !== treeId) {
    lastTreeStartingPersonDataWasFetched.current = treeId
    const noCache = true

    getTreeStartingPid(treeId, noCache)
      .then(({ personId }) => {
        if (isCurrent.current && personId && isTypeTree) {
          // TODO update getTreeStartingPid ReturnType to narrow to PID and TreeId.
          setStartingPersonId(personId as PID)
        }

        return undefined
      })
      .catch(console.error)
      .finally(() => isCurrent.current && setLoading(false))
  }

  const value = useMemo(
    () =>
      ({
        loading,
        startingPersonId,
        setStartingPersonId,
        refetchStartingPersonPid,
        startingPersonOverlay,
      }) satisfies StartingPersonValue,
    [refetchStartingPersonPid, startingPersonId, loading, startingPersonOverlay]
  )
  return (
    <>
      <StartingPersonContext.Provider value={value}>{children}</StartingPersonContext.Provider>
      {(startingPersonOverlay.isOpen || startingPersonOverlay.transitioning) && (
        <SelectPersonOverlay mode="startingPerson" onSelect={onSelectStartingPerson} overlay={startingPersonOverlay} />
      )}
    </>
  )
}
